"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useSearchParams } from "next/navigation";
import { CheckIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { usePageTitle } from "@/lib/usePageTitle";
import { useCheckout } from "@/hooks/useCheckout";
import { toast } from "@/hooks/useToast";
import {
  PRICING_PLANS,
  SERVER_SLOT_PLANS,
  TRIAL_DAYS,
  type PricingPlanId,
  type ServerSlotPlanId,
  getServerLimitForSubscription,
  type SubscriptionStatus,
  type SubscriptionTier,
} from "@/lib/billing/plans";

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatMoney(cents: number) {
  return (cents / 100).toFixed(2);
}

function formatMoneyDollars(amount: number) {
  return amount.toFixed(2);
}

function getCurrentTierLabel(tier: SubscriptionTier) {
  if (tier === "free") return "Free";
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function getUpgradeMessage(reason: string | null) {
  switch (reason) {
    case "auth_required":
      return "Sign in and subscribe to unlock premium BlockyNetwork features.";
    case "grace_period":
      return "Your grace period is active. Renew now to keep premium access uninterrupted.";
    case "canceled_but_in_period":
      return "Your plan is set to cancel at period end. Renew now to keep premium access.";
    case "no_active_subscription":
    case "user_not_found":
      return "An active subscription is required for this feature.";
    default:
      return "Upgrade to access premium BlockyNetwork features.";
  }
}

/** Tier ordering for upgrade/downgrade CTA logic */
const TIER_ORDER: Record<string, number> = {
  free: 0,
  starter: 1,
  plus: 2,
  pro: 3,
  enterprise: 4,
};

// ── Plan Card ────────────────────────────────────────────────────────────────

type MergedPlan = (typeof PRICING_PLANS)[number] & {
  /** Live price from Convex productCatalog (cents), if available */
  livePriceCents?: number;
  /** Polar product ID from the catalog, used to validate checkout routing */
  catalogPolarProductId?: string;
  /** Polar price ID from the catalog */
  catalogPolarPriceId?: string;
  /** Live product name from Polar, if different from static */
  liveName?: string;
  /** Live product description from Polar */
  liveDescription?: string;
};

function PlanCard({
  plan,
  isSignedIn,
  currentTier,
  currentStatus,
  isTrialEligible,
  hasUsedTrial,
  onSelectPlan,
  onStripeCheckout,
  isLoading,
  isStripeLoading,
}: {
  plan: MergedPlan;
  isSignedIn: boolean;
  currentTier: SubscriptionTier;
  currentStatus: SubscriptionStatus;
  isTrialEligible: boolean;
  hasUsedTrial: boolean;
  onSelectPlan: (planId: PricingPlanId) => void;
  onStripeCheckout: (planId: PricingPlanId) => void;
  isLoading: boolean;
  isStripeLoading: boolean;
}) {
  const isActive = currentStatus === "active" || currentStatus === "trialing";
  const isCurrent = isActive && currentTier === (plan.id as SubscriptionTier);

  const isStarterPlan = plan.id === "starter";
  const isUpgrade =
    isActive &&
    !isCurrent &&
    (TIER_ORDER[plan.id] ?? 0) > (TIER_ORDER[currentTier] ?? 0);
  const isDowngrade =
    isActive &&
    !isCurrent &&
    (TIER_ORDER[plan.id] ?? 0) < (TIER_ORDER[currentTier] ?? 0);

  // Display price: prefer live Convex data, fall back to static
  const displayPrice =
    plan.livePriceCents != null
      ? formatMoney(plan.livePriceCents)
      : formatMoneyDollars(plan.priceMonthly);

  // CTA label logic: Current > Loading > Trial > Upgrade > Downgrade > Get Started
  const buttonLabel = isCurrent
    ? "Current Plan"
    : isLoading
      ? "Loading..."
      : isTrialEligible
        ? `Start ${TRIAL_DAYS}-Day Free Trial`
        : isUpgrade
          ? "Upgrade"
          : isDowngrade
            ? "Downgrade"
            : "Get Started";

  const button = isSignedIn ? (
    <div className="flex flex-col gap-2">
      <Button
        className={cn(
          "w-full",
          isTrialEligible && "bg-green-600 hover:bg-green-700"
        )}
        disabled={isCurrent || isLoading}
        onClick={() => onSelectPlan(plan.id as PricingPlanId)}
      >
        {buttonLabel}
      </Button>
      <Button
        variant="outline"
        className="w-full"
        disabled={isCurrent || isStripeLoading}
        onClick={() => onStripeCheckout(plan.id as PricingPlanId)}
      >
        {isStripeLoading ? "Loading..." : "Pay with Stripe"}
      </Button>
    </div>
  ) : (
    <Link href="/signup" className="block">
      <Button className="w-full">
        {isStarterPlan ? "Start Free Trial" : "Get Started"}
      </Button>
    </Link>
  );

  return (
    <div
      className={cn(
        "relative flex flex-col rounded-xl border border-border bg-background/40 p-6 shadow-sm backdrop-blur",
        plan.badge ? "ring-1 ring-primary/40" : "",
        isCurrent && "border-primary bg-primary/5"
      )}
    >
      {plan.badge && (
        <div className="absolute right-6 top-6 rounded-full bg-primary/15 px-3 py-1 text-xs font-medium text-primary">
          {plan.badge}
        </div>
      )}

      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-semibold text-foreground">
            {plan.liveName ?? plan.name}
          </h3>
          {isCurrent && (
            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
              Your plan
            </span>
          )}
        </div>
        <p className="text-sm text-muted">{plan.liveDescription ?? plan.description}</p>
      </div>

      <div className="mt-6 flex items-end gap-2">
        <p className="text-4xl font-bold text-foreground">${displayPrice}</p>
        <p className="pb-1 text-sm text-muted">/mo</p>
      </div>

      {/* Trial callout */}
      {isTrialEligible ? (
        <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 dark:border-green-800 dark:bg-green-900/20">
          <p className="text-sm font-medium text-green-700 dark:text-green-300">
            First {TRIAL_DAYS} days free — no charge until trial ends
          </p>
        </div>
      ) : isStarterPlan && !isSignedIn ? (
        <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 dark:border-green-800 dark:bg-green-900/20">
          <p className="text-sm font-medium text-green-700 dark:text-green-300">
            {TRIAL_DAYS}-day free trial for new subscribers
          </p>
        </div>
      ) : isStarterPlan && isSignedIn && hasUsedTrial ? (
        <p className="mt-3 text-xs text-muted">
          Trial already used on this account
        </p>
      ) : null}

      <ul className="mt-5 flex-1 space-y-3 text-sm text-muted">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8">{button}</div>
    </div>
  );
}

// ── Server Slot Plan Card ─────────────────────────────────────────────────────

type MergedServerSlotPlan = (typeof SERVER_SLOT_PLANS)[number] & {
  livePriceCents?: number;
  liveName?: string;
  liveDescription?: string;
};

function ServerSlotPlanCard({
  plan,
  isSignedIn,
  onSelectPlan,
  isLoading,
}: {
  plan: MergedServerSlotPlan;
  isSignedIn: boolean;
  onSelectPlan: (planId: ServerSlotPlanId) => void;
  isLoading: boolean;
}) {
  // Display price: prefer live Convex data, fall back to static
  const displayPrice =
    plan.livePriceCents != null
      ? formatMoney(plan.livePriceCents)
      : formatMoneyDollars(plan.priceMonthly);

  const button = isSignedIn ? (
    <Button
      className={cn("w-full", plan.badge ? "bg-green-600 hover:bg-green-700" : "")}
      disabled={isLoading}
      onClick={() => onSelectPlan(plan.id)}
    >
      {isLoading ? "Loading..." : "Subscribe"}
    </Button>
  ) : (
    <Link href="/signup" className="block">
      <Button className="w-full">Subscribe</Button>
    </Link>
  );

  return (
    <div
      className={cn(
        "relative flex flex-col rounded-xl border border-border bg-background/40 p-6 shadow-sm backdrop-blur",
        plan.badge ? "ring-1 ring-primary/40" : ""
      )}
    >
      {plan.badge && (
        <div className="absolute right-6 top-6 rounded-full bg-primary/15 px-3 py-1 text-xs font-medium text-primary">
          {plan.badge}
        </div>
      )}

      <div className="space-y-1">
        <h3 className="text-lg font-semibold text-foreground">{plan.liveName ?? plan.name}</h3>
        <p className="text-sm text-muted">{plan.liveDescription ?? plan.description}</p>
      </div>

      <div className="mt-6 flex items-end gap-2">
        <p className="text-4xl font-bold text-foreground">${displayPrice}</p>
        <p className="pb-1 text-sm text-muted">/mo per server</p>
      </div>

      {/* Server specs */}
      <div className="mt-4 grid grid-cols-2 gap-2 rounded-lg border border-border bg-foreground/[0.03] p-3 text-xs text-muted">
        <div><span className="font-medium text-foreground">{plan.specs.cpu}</span></div>
        <div><span className="font-medium text-foreground">{plan.specs.ram}</span></div>
        <div><span className="font-medium text-foreground">{plan.specs.storage}</span></div>
        <div><span className="font-medium text-foreground">{plan.specs.bandwidth}</span></div>
      </div>

      <ul className="mt-5 flex-1 space-y-3 text-sm text-muted">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8">{button}</div>
    </div>
  );
}

// ── FAQ ──────────────────────────────────────────────────────────────────────

const FAQ_ITEMS = [
  {
    q: "What happens after my free trial ends?",
    a: `Your ${TRIAL_DAYS}-day trial converts to a paid Starter subscription at $3.99/mo. You can cancel anytime before the trial ends and you won't be charged.`,
  },
  {
    q: "Can I change plans later?",
    a: "Yes. You can upgrade or downgrade at any time from your billing settings. Plan changes take effect immediately and billing is prorated.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept all major credit and debit cards through Polar.sh or Stripe. You can choose your preferred payment provider at checkout.",
  },
  {
    q: "How do I cancel my subscription?",
    a: "Go to Dashboard > Settings > Billing and click \"Cancel subscription\". Your access continues until the end of the current billing period.",
  },
  {
    q: "What counts as an organization?",
    a: "An organization is a server community you create on BlockyNetworks. Each one can have its own members, chat, server listing, and plugin integration.",
  },
  {
    q: "Do I need the Java plugin to use BlockyNetworks?",
    a: "The Java plugin is optional but recommended. It enables server heartbeats, player account linking, and real-time online player counts. You can create and manage your community without it.",
  },
  {
    q: "Is there an Enterprise plan?",
    a: "Yes. For networks with 10+ servers, contact us for custom Enterprise pricing with dedicated support and higher server limits.",
  },
  {
    q: "What is the refund policy?",
    a: "We offer a full refund within the first 7 days of any paid subscription. See our refund policy page for details.",
  },
] as const;

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-4 py-4 text-left text-sm font-medium text-foreground hover:text-primary transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span>{q}</span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          className={cn(
            "h-4 w-4 shrink-0 text-muted transition-transform duration-200",
            open && "rotate-180"
          )}
        >
          <path
            d="M6 9l6 6 6-6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <p className="pb-4 text-sm leading-relaxed text-muted">{a}</p>
      )}
    </div>
  );
}

// ── Comparison Table ─────────────────────────────────────────────────────────

type ComparisonRow = {
  feature: string;
  starter: string | boolean;
  plus: string | boolean;
  pro: string | boolean;
};

function ComparisonTable({
  rows,
  livePrices,
}: {
  rows: ComparisonRow[];
  livePrices: { starter?: number; plus?: number; pro?: number };
}) {
  const renderCell = (value: string | boolean) => {
    if (value === true) {
      return (
        <CheckIcon className="mx-auto h-4 w-4 text-primary" />
      );
    }
    if (value === false) {
      return <span className="text-muted">&mdash;</span>;
    }
    return <span>{value}</span>;
  };

  // Build price row with live data override
  const priceRow: ComparisonRow = {
    feature: "Monthly Price",
    starter: livePrices.starter != null ? `$${formatMoney(livePrices.starter)}` : "$3.99",
    plus: livePrices.plus != null ? `$${formatMoney(livePrices.plus)}` : "$7.99",
    pro: livePrices.pro != null ? `$${formatMoney(livePrices.pro)}` : "$12.99",
  };

  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-foreground/[0.03]">
            <th className="px-4 py-3 text-left font-medium text-muted">
              Feature
            </th>
            <th className="px-4 py-3 text-center font-medium text-muted">
              Starter
            </th>
            <th className="px-4 py-3 text-center font-medium text-muted">
              Plus
            </th>
            <th className="px-4 py-3 text-center font-medium text-muted">
              Pro
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => (
            <tr key={row.feature}>
              <td className="px-4 py-3 text-foreground">{row.feature}</td>
              <td className="px-4 py-3 text-center text-foreground">
                {renderCell(row.starter)}
              </td>
              <td className="px-4 py-3 text-center text-foreground">
                {renderCell(row.plus)}
              </td>
              <td className="px-4 py-3 text-center text-foreground">
                {renderCell(row.pro)}
              </td>
            </tr>
          ))}
          {/* Price row — always last */}
          <tr>
            <td className="px-4 py-3 text-foreground">{priceRow.feature}</td>
            <td className="px-4 py-3 text-center font-medium text-foreground">
              {priceRow.starter}
            </td>
            <td className="px-4 py-3 text-center font-medium text-foreground">
              {priceRow.plus}
            </td>
            <td className="px-4 py-3 text-center font-medium text-foreground">
              {priceRow.pro}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Comparison data ──────────────────────────────────────────────────────────

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "Organizations / Communities", starter: "1", plus: "5", pro: "10" },
  { feature: "Server Finder Listing", starter: true, plus: true, pro: true },
  { feature: "Plugin Integration", starter: true, plus: true, pro: true },
  { feature: "Player Account Linking", starter: true, plus: true, pro: true },
  { feature: "Server Heartbeats", starter: true, plus: true, pro: true },
  { feature: "Community Chat", starter: true, plus: true, pro: true },
  { feature: "Vote Rewards", starter: true, plus: true, pro: true },
  { feature: "Advanced Stats", starter: false, plus: true, pro: true },
  { feature: "Priority Queue", starter: false, plus: false, pro: true },
  { feature: "Priority Support", starter: false, plus: false, pro: true },
];

// ── Page ─────────────────────────────────────────────────────────────────────

export default function PricingPage() {
  usePageTitle("Pricing");
  const searchParams = useSearchParams();
  const { isSignedIn, isLoaded } = useAuth();
  const signedIn = !!isSignedIn;

  // Live product catalog from Convex (synced from Polar.sh)
  const catalogProducts = useQuery(api.productCatalog.listActive);

  // Trigger a fresh Polar.sh product catalog sync on page mount so pricing
  // data is never more than a page-load old. The useQuery above reacts to the
  // updated Convex DB records automatically once the sync completes.
  const triggerCatalogSync = useAction(api.actions.triggerProductCatalogSync);
  useEffect(() => {
    triggerCatalogSync({}).catch((err) => {
      console.warn("[PricingPage] catalog sync failed (static fallback active):", err);
    });
    // Run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const user = useQuery(api.users.getCurrentUser);
  const billingSummary = useQuery(
    api.billing.getMySummary,
    signedIn ? {} : "skip"
  );
  const createStripeCheckoutSession = useAction(
    api.actions.createStripeCheckoutSession
  );
  const createServerSlotCheckout = useAction(
    api.actions.createSubscriptionCheckout
  );
  const {
    startCheckout,
    loadingPlan,
    error: checkoutError,
    clearError,
  } = useCheckout();
  const [stripeLoadingPlan, setStripeLoadingPlan] =
    useState<PricingPlanId | null>(null);
  const [stripeError, setStripeError] = useState("");
  const [serverSlotLoadingPlan, setServerSlotLoadingPlan] =
    useState<ServerSlotPlanId | null>(null);
  const [serverSlotError, setServerSlotError] = useState("");

  // Surface Polar checkout errors as toasts
  useEffect(() => {
    if (checkoutError) {
      toast.error(checkoutError);
    }
  }, [checkoutError]);

  const currentTier = (user?.subscriptionTier ?? "free") as SubscriptionTier;
  const currentStatus = (user?.subscriptionStatus ?? "none") as SubscriptionStatus;
  const serverLimit = getServerLimitForSubscription(currentTier, currentStatus);
  const hasUsedTrial = billingSummary?.hasUsedTrial ?? false;
  const isOnPaidPlan =
    currentTier !== "free" &&
    (currentStatus === "active" || currentStatus === "trialing");
  const showUpgradeBanner = searchParams.get("upgrade") === "1";
  const upgradeMessage = getUpgradeMessage(searchParams.get("reason"));
  const showCanceledBanner = searchParams.get("canceled") === "true";

  // Merge live catalog pricing into static plan definitions
  const catalogByTier = new Map(
    (catalogProducts ?? []).map((p: any) => [p.tier, p])
  );

  const mergedPlans: MergedPlan[] = PRICING_PLANS.map((plan) => {
    const live = catalogByTier.get(plan.id);
    return {
      ...plan,
      livePriceCents: live?.priceAmountCents,
      catalogPolarProductId: live?.polarProductId,
      catalogPolarPriceId: live?.polarPriceId,
      liveName: live?.name,
      liveDescription: live?.description,
    };
  });

  // Merge live pricing into Server Slot Plans
  const mergedServerSlotPlans: MergedServerSlotPlan[] = SERVER_SLOT_PLANS.map((plan) => {
    const live = catalogByTier.get(plan.id);
    return {
      ...plan,
      livePriceCents: live?.priceAmountCents,
      liveName: live?.name,
      liveDescription: live?.description,
    };
  });

  // Live prices for comparison table
  const livePrices = {
    starter: catalogByTier.get("starter")?.priceAmountCents,
    plus: catalogByTier.get("plus")?.priceAmountCents,
    pro: catalogByTier.get("pro")?.priceAmountCents,
  };

  // Catalog loading state — show subtle indicator while data loads
  const isCatalogLoading = catalogProducts === undefined;

  const handleSelectPlan = (planId: PricingPlanId) => {
    clearError();
    const isTrialEligible =
      planId === "starter" && !hasUsedTrial && currentTier === "free";
    const nextParam = searchParams.get("next");
    const safeNext =
      nextParam && /^\/[^/]/.test(nextParam) ? nextParam : undefined;
    startCheckout(planId, {
      withTrial: isTrialEligible,
      successRedirect: safeNext ?? "/dashboard",
      onPlanUpdated: () => {
        toast.success("Plan updated successfully!");
        window.location.href = "/dashboard?checkout=success";
      },
    });
  };

  const handleStripeCheckout = async (planId: PricingPlanId) => {
    setStripeError("");
    setStripeLoadingPlan(planId);
    try {
      const result = await createStripeCheckoutSession({ plan: planId });
      if (!result.ok) {
        throw new Error(
          result.error?.message || "Failed to start Stripe checkout."
        );
      }
      if (!result.data?.checkoutUrl) {
        throw new Error("Missing Stripe checkout URL.");
      }
      window.location.href = result.data.checkoutUrl;
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to start Stripe checkout.";
      setStripeError(msg);
      toast.error(msg);
    } finally {
      setStripeLoadingPlan(null);
    }
  };

  const handleServerSlotCheckout = async (planId: ServerSlotPlanId) => {
    setServerSlotError("");
    setServerSlotLoadingPlan(planId);
    try {
      const result = await createServerSlotCheckout({ plan: planId });
      if (!result.ok) {
        throw new Error(result.error?.message || "Failed to start checkout.");
      }
      if (result.data?.updated) {
        window.location.href = "/dashboard/settings/billing?checkout=success";
        return;
      }
      if (result.data?.checkoutUrl) {
        window.location.href = result.data.checkoutUrl;
        return;
      }
      throw new Error("No checkout URL returned. Please try again.");
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to start checkout.";
      setServerSlotError(msg);
      toast.error(msg);
    } finally {
      setServerSlotLoadingPlan(null);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="relative flex-1">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(60%_40%_at_50%_0%,rgba(21,128,61,0.20),transparent_60%)]" />

        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          {/* ── Header ─────────────────────────────────────── */}
          <div className="mx-auto max-w-3xl text-center">
            {showCanceledBanner && (
              <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
                Checkout was canceled. No charge was made. You can pick a plan
                below whenever you&apos;re ready.
              </div>
            )}
            {showUpgradeBanner && (
              <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
                {upgradeMessage}
              </div>
            )}
            <h1 className="text-balance text-4xl font-bold text-foreground sm:text-5xl">
              Unlock Server Tools
            </h1>
            <p className="mt-4 text-pretty text-base text-muted sm:text-lg">
              Free accounts are great for browsing. Upgrade to create a
              community, post your server, and connect the plugin in minutes.
            </p>

            <div className="mt-4 text-sm text-muted">
              Includes{" "}
              <span className="font-medium text-foreground">1 community</span>{" "}
              &bull; Cancel anytime
            </div>

            {/* Current plan info for signed-in users */}
            {isLoaded && user && (
              <div className="mt-6 rounded-lg border border-border bg-background/50 px-4 py-3 text-sm text-muted">
                {isOnPaidPlan ? (
                  <>
                    <span className="font-medium text-foreground">
                      {getCurrentTierLabel(currentTier)} plan
                    </span>{" "}
                    &bull; {serverLimit}{" "}
                    {serverLimit === 1 ? "server" : "servers"} &bull;{" "}
                    <Link
                      href="/dashboard/settings/billing"
                      className="text-primary hover:underline"
                    >
                      Manage subscription &rarr;
                    </Link>
                  </>
                ) : (
                  <>
                    <span className="font-medium text-foreground">
                      Free plan
                    </span>{" "}
                    &bull; Subscribe below to unlock server tools
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Plan Cards ─────────────────────────────────── */}
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            {(checkoutError || stripeError) && (
              <div className="lg:col-span-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {checkoutError || stripeError}
              </div>
            )}
            {isCatalogLoading && (
              <div className="lg:col-span-3 rounded-md border border-border bg-foreground/[0.02] px-4 py-2 text-xs text-muted text-center">
                Loading live pricing from Polar.sh&hellip;
              </div>
            )}
            {mergedPlans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isSignedIn={signedIn}
                currentTier={currentTier}
                currentStatus={currentStatus}
                hasUsedTrial={hasUsedTrial}
                isTrialEligible={
                  signedIn &&
                  plan.id === "starter" &&
                  !hasUsedTrial &&
                  currentTier === "free"
                }
                onSelectPlan={handleSelectPlan}
                onStripeCheckout={handleStripeCheckout}
                isLoading={loadingPlan === plan.id}
                isStripeLoading={stripeLoadingPlan === plan.id}
              />
            ))}
          </div>

          {/* ── Server Slot Plans ──────────────────────────── */}
          <div className="mt-20">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-2xl font-bold text-foreground">
                Managed Server Plans
              </h2>
              <p className="mt-3 text-base text-muted">
                Auto-provisioned dedicated game servers — managed for you on Vultr. Pay monthly, cancel anytime.
              </p>
            </div>

            {serverSlotError && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                {serverSlotError}
              </div>
            )}

            <div className="mt-8 grid gap-6 lg:grid-cols-3">
              {mergedServerSlotPlans.map((plan) => (
                <ServerSlotPlanCard
                  key={plan.id}
                  plan={plan}
                  isSignedIn={signedIn}
                  onSelectPlan={handleServerSlotCheckout}
                  isLoading={serverSlotLoadingPlan === plan.id}
                />
              ))}
            </div>
          </div>

          {/* ── Feature Comparison Table ───────────────────── */}
          <div className="mt-16">
            <h2 className="mb-6 text-center text-2xl font-bold text-foreground">
              Feature Comparison
            </h2>
            <ComparisonTable rows={COMPARISON_ROWS} livePrices={livePrices} />
          </div>

          {/* ── FAQ ────────────────────────────────────────── */}
          <div className="mt-16">
            <h2 className="mb-6 text-center text-2xl font-bold text-foreground">
              Frequently Asked Questions
            </h2>
            <div className="mx-auto max-w-2xl rounded-xl border border-border bg-background/40 px-6 backdrop-blur">
              {FAQ_ITEMS.map((item) => (
                <FAQItem key={item.q} q={item.q} a={item.a} />
              ))}
            </div>
          </div>

          {/* ── Bottom CTA ─────────────────────────────────── */}
          <div className="mt-10 text-center text-sm text-muted">
            Already subscribed?{" "}
            {signedIn ? (
              <Link
                href="/dashboard/settings/billing"
                className="text-primary hover:underline"
              >
                Manage your subscription &rarr;
              </Link>
            ) : (
              <Link href="/login" className="text-primary hover:underline">
                Sign in to manage billing
              </Link>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
